import { useParams } from "@/lib/router-compat";
import FallbackTitle from "@/components/FallbackTitle";
import { useQuery } from "@tanstack/react-query";
import { pageQueryOptions } from "@/lib/pageQuery";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Skeleton } from "@/components/ui/skeleton";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import parse from "html-react-parser";
import { sanitizeHtml } from "@/lib/sanitizeHtml";
import NotFound from "./NotFound";

// Detect if content is HTML or Markdown
const isHtmlContent = (content: string | null): boolean => {
  if (!content) return false;
  return /<(p|div|figure|img|a|ul|ol|h[1-6]|table|blockquote|span)\b[^>]*>/i.test(content);
};

// Render content with sanitization
const renderContent = (content: string | null | undefined) => {
  if (!content) return null;
  
  if (isHtmlContent(content)) {
    // Sanitize HTML before parsing
    const sanitizedContent = sanitizeHtml(content);
    return parse(sanitizedContent);
  }
  
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]}>
      {content}
    </ReactMarkdown>
  );
};

export default function Page() {
  const { slug } = useParams();

  const { data: page, isLoading, error } = useQuery({
    ...pageQueryOptions(slug || ""),
    enabled: Boolean(slug),
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <FallbackTitle />
        <Header />
        <main className="container mx-auto px-4 py-12 max-w-4xl">
          <Skeleton className="h-12 w-3/4 mb-4" />
          <Skeleton className="h-6 w-1/2 mb-8" />
          <Skeleton className="h-64 w-full mb-4" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-3/4" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!page || error) {
    return <NotFound />;
  }

  const renderTemplate = () => {
    switch (page.template) {
      case "landing":
        return (
          <div className="min-h-screen bg-background">
            <Header />
            {page.featured_image && (
              <div 
                className="relative h-[50vh] bg-cover bg-center"
                style={{ backgroundImage: `url(${page.featured_image})` }}
              >
                <div className="absolute inset-0 bg-linear-to-b from-background/60 to-background" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center px-4">
                    <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-4">
                      {page.title}
                    </h1>
                    {page.excerpt && (
                      <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                        {page.excerpt}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            <main className="container mx-auto px-4 py-12 max-w-4xl">
              {!page.featured_image && (
                <h1 className="text-4xl font-bold text-foreground mb-8">{page.title}</h1>
              )}
              <div className="prose prose-lg dark:prose-invert max-w-none">
                {renderContent(page.content)}
              </div>
            </main>
            <Footer />
          </div>
        );

      case "full-width":
        return (
          <div className="min-h-screen bg-background">
            <Header />
            <main className="py-12">
              <div className="container mx-auto px-4 mb-8">
                <h1 className="text-4xl font-bold text-foreground">{page.title}</h1>
                {page.excerpt && (
                  <p className="text-xl text-muted-foreground mt-4">{page.excerpt}</p>
                )}
              </div>
              {page.featured_image && (
                <img
                  src={page.featured_image}
                  alt={page.featured_image_alt || page.title}
                  className="w-full h-[400px] object-cover mb-8"
                />
              )}
              <div className="container mx-auto px-4">
                <div className="prose prose-lg dark:prose-invert max-w-none">
                  {renderContent(page.content)}
                </div>
              </div>
            </main>
            <Footer />
          </div>
        );

      case "minimal":
        return (
          <div className="min-h-screen bg-background">
            <Header />
            <main className="container mx-auto px-4 py-16 max-w-3xl">
              <h1 className="text-3xl font-bold text-foreground mb-8 text-center">
                {page.title}
              </h1>
              <div className="prose dark:prose-invert max-w-none">
                {renderContent(page.content)}
              </div>
            </main>
            <Footer />
          </div>
        );

      default:
        return (
          <div className="min-h-screen bg-background">
            <Header />
            <main className="container mx-auto px-4 py-12 max-w-4xl">
              <article>
                <header className="mb-8">
                  <h1 className="text-4xl font-bold text-foreground mb-4">
                    {page.title}
                  </h1>
                  {page.excerpt && (
                    <p className="text-xl text-muted-foreground">{page.excerpt}</p>
                  )}
                </header>

                {page.featured_image && (
                  <img
                    src={page.featured_image}
                    alt={page.featured_image_alt || page.title}
                    className="w-full h-[400px] object-cover rounded-lg mb-8"
                  />
                )}

                <div className="prose prose-lg dark:prose-invert max-w-none">
                  {renderContent(page.content)}
                </div>
              </article>
            </main>
            <Footer />
          </div>
        );
    }
  };

  return (
    <>
      {renderTemplate()}
    </>
  );
}
